import { NavigationEnd, Router } from '@angular/router';
import { Injectable } from '@angular/core';
import { ConfigJsonService, LogService } from '@medlogic/shared/shared-interfaces';
import { MatSnackBar } from '@angular/material/snack-bar';

@Injectable({
  providedIn: 'root'
})
export class TimeoutService {
  private timeoutId: any;
  private activityTimeoutId: any;
  private warningTimeout10minId: any;
  private warningTimeout1minId: any;
  private monitoringStarted = false;
  private resetHandler: () => void;
  private readonly activityEvents = ['mousemove', 'keydown', 'click', 'scroll'];
  private readonly timeoutInMilliseconds = this.cnf.getTimeoutForSession(); // ⏳ Padrão de 30 minutos
  private readonly activityDetectionDelay = 5000;
  private onTimeoutCallback: () => void = () => this.defaultLogout();

  constructor(
    private log: LogService,
    private router: Router,
    private snackBar: MatSnackBar,
    private cnf: ConfigJsonService
  ) { }

  startMonitoring(onTimeout?: () => void): void {
    try {
      if (onTimeout) {
        this.onTimeoutCallback = onTimeout;
      }

      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          const currentRoute = this.router.url || '';
          const isLoginOrExamRoute = currentRoute.includes('login') || currentRoute.endsWith('exam');

          this.clearAllTimeouts();

          if (isLoginOrExamRoute && this.monitoringStarted) {
            this.monitoringStarted = false;
            this.clearEventListeners();
          } else if (!isLoginOrExamRoute && !this.monitoringStarted) {
            this.monitoringStarted = true;
            this.setupActivityListeners();
          }
        }
      });
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'startMonitoring', error.message);
    }
  }

  private setupActivityListeners(): void {
    try {
      this.resetHandler = () => {
        this.clearAllTimeouts();

        if (!this.activityTimeoutId) {
          this.activityTimeoutId = setTimeout(() => {
            this.scheduleTimeout();
          }, this.activityDetectionDelay);
        }
      };

      this.activityEvents.forEach(event =>
        window.addEventListener(event, this.resetHandler)
      );
    } catch (error) {
      this.log.Registrar(this.constructor.name, 'setupActivityListeners', error.message);
    }
  }

  private scheduleTimeout(): void {
    const warningTime10min = this.timeoutInMilliseconds - 10 * 60 * 1000;
    const warningTime1min = this.timeoutInMilliseconds - 1 * 60 * 1000;

    if (warningTime10min > 0) {
      this.warningTimeout10minId = setTimeout(() => {
        this.snackBar.open('Sua sessão irá expirar em 10 minutos por inatividade.', 'OK', {
          duration: 30000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
          panelClass: 'warning-snackbar'
        });
      }, warningTime10min);
    }

    if (warningTime1min > 0) {
      this.warningTimeout1minId = setTimeout(() => {
        this.snackBar.open('Sua sessão irá expirar em 1 minuto por inatividade.', 'OK', {
          duration: 30000,
          verticalPosition: 'top',
          horizontalPosition: 'center',
          panelClass: 'warning-snackbar'
        });
      }, warningTime1min);
    }

    this.timeoutId = setTimeout(() => {
      if (this.resetHandler) {
        this.clearEventListeners();
      }

      this.onTimeoutCallback();
      alert('Sessão expirada! Você será deslogado.');
    }, this.timeoutInMilliseconds);
  }

  clearAllTimeouts(): void {
    if (this.timeoutId != null) {
      clearTimeout(this.timeoutId);
      this.timeoutId = null;
    }

    if (this.activityTimeoutId != null) {
      clearTimeout(this.activityTimeoutId);
      this.activityTimeoutId = null;
    }

    if (this.warningTimeout10minId != null) {
      clearTimeout(this.warningTimeout10minId);
      this.warningTimeout10minId = null;
    }

    if (this.warningTimeout1minId != null) {
      clearTimeout(this.warningTimeout1minId);
      this.warningTimeout1minId = null;
    }
  }

  clearEventListeners(): void {
    this.activityEvents.forEach(event =>
      window.removeEventListener(event, this.resetHandler)
    );
  }

  private defaultLogout(): void {
    this.snackBar.open('⏳ Sessão expirada! Você será deslogado.', '', {
      duration: 5000,
      verticalPosition: 'top'
    });
    this.router.navigate(['/login']);
  }
}
